import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, catchError, combineLatest, forkJoin, map, of, startWith, switchMap } from 'rxjs';
import { DivineServiceApiService, DivineServiceListDto } from '@core/api/services/divine-service-api.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { PersonService } from '@features/people/services/person.service';
import { UnitService } from '@features/units/services/unit.service';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { UserFullInfoDto } from '@shared/models/user-full-info.model';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';

interface CardState<T> { data: T; loading: boolean; error: boolean; }

function cardState<T>(source: Observable<T>, empty: T): Observable<CardState<T>> {
  return source.pipe(
    map(data => ({ data, loading: false, error: false })),
    startWith({ data: empty, loading: true, error: false }),
    catchError(() => of({ data: empty, loading: false, error: true })),
  );
}

function localDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'app-home',
  imports: [CommonModule, RouterLink, MatButtonModule, MatIconModule, MatFormFieldModule, MatSelectModule, LoadingStateComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  private readonly userInfo = inject(UserFullInfoService);
  private readonly people = inject(PersonService);
  private readonly services = inject(DivineServiceApiService);
  private readonly unitService = inject(UnitService);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentUser = signal<UserFullInfoDto | null>(null);
  readonly units = signal<OrganizationUnitItem[]>([]);
  readonly selectedUnitId = signal<string | null>(null);
  readonly unitsLoading = signal(true);
  readonly unitsError = signal(false);
  private readonly readableIds = signal<string[]>([]);
  private readonly serviceManageableIds = signal<string[]>([]);
  private readonly reload = signal(0);

  readonly selectedUnit = computed(() => this.units().find(unit => unit.id === this.selectedUnitId()) ?? null);
  readonly canViewInternal = computed(() => this.userInfo.canViewRestrictedDataSnapshot(this.currentUser()));
  readonly canReadSelected = computed(() => this.canViewInternal() && this.contains(this.readableIds(), this.selectedUnitId()));
  readonly canCreateService = computed(() => this.userInfo.canManageDivineServicesSnapshot(this.currentUser()) &&
    this.contains(this.serviceManageableIds(), this.selectedUnitId()));
  readonly canCreateMember = computed(() => this.userInfo.canManageMemberDataSnapshot(this.currentUser()) &&
    this.contains(this.readableIds(), this.selectedUnitId()) &&
    (!!this.currentUser()?.isAdmin || this.currentUser()?.manageableUnitIds.some(id => this.sameId(id, this.selectedUnitId()))));
  readonly greetingName = computed(() => this.currentUser()?.firstName || this.currentUser()?.fullName || '');
  readonly hasResponsibilities = computed(() => {
    const user = this.currentUser();
    return !!(user?.activeMinistry || user?.activeAppointments.length || user?.activeAssignments.length || user?.activeActivities.length);
  });
  readonly roleLabels = computed(() => {
    const user = this.currentUser();
    if (user?.isAdmin) return ['Administrador'];
    const labels: Record<string, string> = {
      unitmanager: 'Responsable de unidad', servant: 'Siervo',
      ministerialmanager: 'Gestión ministerial regional', member: 'Miembro',
    };
    return user?.roles.length ? user.roles.map(role => labels[role.toLowerCase()] ?? role) : ['Miembro'];
  });
  private readonly scope = computed(() => ({ id: this.selectedUnitId(), readable: this.canReadSelected(), reload: this.reload() }));

  readonly dashboard = toSignal(toObservable(this.scope).pipe(switchMap(scope => {
    if (!scope.id || !scope.readable) return of(null);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const list = (start: string | null, direction: string, size: number) =>
      this.services.getDivineServices(scope.id, start, localDate(start ? tomorrow : today), 'date', direction, 1, size).pipe(
        map(response => response.items ?? []));
    return combineLatest({
      agenda: cardState(list(localDate(today), 'asc', 3), []),
      latest: cardState(list(null, 'desc', 1).pipe(map(items => items[0] ?? null)), null),
    });
  })), { initialValue: null });

  readonly latestServiceRoute = computed(() => {
    const service = this.dashboard()?.latest.data;
    return service ? this.serviceRoute(service) : ['/units', this.selectedUnitId()!, 'divine-services'];
  });

  private readonly pendingScope = computed(() => ({ user: this.currentUser(), allowed: this.canViewInternal(), reload: this.reload() }));
  readonly pending = toSignal(toObservable(this.pendingScope).pipe(switchMap(scope => scope.allowed
    ? cardState(this.unitService.getAllMinistryTransferRequests('Pending').pipe(map(requests => requests.length)), 0)
    : of(null))), { initialValue: null });

  ngOnInit(): void {
    this.userInfo.getUserInfo$().pipe(
      switchMap(user => {
        this.currentUser.set(user);
        this.unitsLoading.set(!!user);
        this.units.set([]);
        this.readableIds.set([]);
        this.serviceManageableIds.set([]);
        if (!user) return of(null);
        return forkJoin({
          readable: this.canViewInternal()
            ? this.people.getAuthorizedUnits().pipe(map(data => ({ data, error: false })),
                catchError(() => of({ data: [] as OrganizationUnitItem[], error: true })))
            : of({ data: [] as OrganizationUnitItem[], error: false }),
          manageable: this.userInfo.canManageDivineServicesSnapshot(user)
            ? this.services.getManageableUnits().pipe(map(data => ({ data: data.filter(unit => !!unit.id).map(unit => ({
                id: unit.id!, name: unit.name ?? '', levelId: unit.organizationLevelId ?? 0,
                levelName: unit.levelName ?? '', ancestorNames: unit.ancestorNames ?? [],
              })), error: false })), catchError(() => of({ data: [] as OrganizationUnitItem[], error: true })))
            : of({ data: [] as OrganizationUnitItem[], error: false }),
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      this.unitsLoading.set(false);
      if (!result) return;
      this.readableIds.set(result.readable.data.map(unit => unit.id.toLowerCase()));
      this.serviceManageableIds.set(result.manageable.data.map(unit => unit.id.toLowerCase()));
      this.unitsError.set(result.readable.error || result.manageable.error);
      const available = new Map<string, OrganizationUnitItem>();
      [...result.readable.data, ...result.manageable.data].forEach(unit => available.set(unit.id.toLowerCase(), unit));
      const community = this.currentUser()?.community;
      if (community && !available.has(community.id.toLowerCase())) available.set(community.id.toLowerCase(), {
        id: community.id, name: community.name, levelId: community.organizationLevelId, levelName: community.levelName,
      });
      const units = [...available.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
      this.units.set(units);
      this.selectedUnitId.set(units.find(unit => this.sameId(unit.id, this.selectedUnitId()))?.id ??
        units.find(unit => this.sameId(unit.id, community?.id))?.id ?? units[0]?.id ?? null);
    });
    void this.userInfo.loadUserInfo().catch(() => undefined);
  }

  retry(): void { this.reload.update(value => value + 1); }

  retryUnits(): void { void this.userInfo.refreshUserInfo().catch(() => undefined); }

  serviceRoute(service: DivineServiceListDto): string[] {
    return ['/units', service.organizationUnitId || this.selectedUnitId()!, 'divine-services', service.id!];
  }

  private contains(ids: string[], id: string | null): boolean { return !!id && ids.includes(id.toLowerCase()); }

  private sameId(left: string, right: string | null | undefined): boolean {
    return !!right && left.toLowerCase() === right.toLowerCase();
  }
}
